import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  CostRadarSubscriptionState,
  useCostRadarSubscriptionByVenueLazyQuery,
  useUpdateCostRadarSubscription,
  useVenuesQuery,
} from '../../generated/graphql';

type VenueOption = { id: string; name: string };

export const CostRadarTrialManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [trialState, setTrialState] = useState<CostRadarSubscriptionState | ''>(
    '',
  );
  const [expirationDate, setExpirationDate] = useState<DateTime | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [
    loadCostRadarData,
    { data: costRadarData, loading: costRadarLoading },
  ] = useCostRadarSubscriptionByVenueLazyQuery();
  const { data: venuesData, loading: venuesLoading } = useVenuesQuery();
  const [updateCostRadarSubscription] = useUpdateCostRadarSubscription();

  const venues: VenueOption[] = useMemo(() => {
    if (!venuesData?.venues) return [];
    const unique = new Map(
      venuesData.venues.map((venue) => [
        venue.id,
        { id: venue.id, name: venue.name },
      ]),
    );
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;

  const currentSubscription = costRadarData?.costRadarSubscriptionByVenue;

  // If no subscription exists, consider it as TrialAvailable
  let currentState =
    currentSubscription?.state || CostRadarSubscriptionState.TrialAvailable;
  const currentExpiresAt = currentSubscription?.trialExpiresAt
    ? DateTime.fromMillis(currentSubscription.trialExpiresAt)
    : null;

  if (
    currentState === CostRadarSubscriptionState.TrialStarted &&
    currentExpiresAt &&
    currentExpiresAt < DateTime.now()
  ) {
    currentState = CostRadarSubscriptionState.TrialExpired;
  }

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    setSelectedVenueId(value?.id ?? '');
    if (value) {
      loadCostRadarData({ variables: { venueId: value.id } });
    }
    // Reset form
    setTrialState('');
    setExpirationDate(null);
  };

  const handleStateChange = (event: { target: { value: string } }) => {
    const newState = event.target.value as CostRadarSubscriptionState;
    setTrialState(newState);

    // If setting to TrialStarted and no current expiration, set default to 14 days from now
    if (
      newState === CostRadarSubscriptionState.TrialStarted &&
      !currentExpiresAt
    ) {
      setExpirationDate(DateTime.now().plus({ days: 14 }));
    }
  };

  const openConfirmDialog = () => {
    if (!selectedVenueId || !trialState) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedVenueId || !trialState) return;

    try {
      await updateCostRadarSubscription({
        variables: {
          venueId: selectedVenueId,
          state: trialState,
          ...(expirationDate && { trialExpiresAt: expirationDate.toMillis() }),
        },
        toastConfig: {
          successText: 'Cost radar subscription updated',
          errorText: 'Failed to update cost radar subscription',
        },
      });

      // Reload data to show updated state
      loadCostRadarData({ variables: { venueId: selectedVenueId } });

      setConfirmOpen(false);
      setTrialState('');
      setExpirationDate(null);
    } catch (error) {
      console.error('Failed to update cost radar subscription:', error);
    }
  };

  const getStateLabel = (state: CostRadarSubscriptionState) => {
    switch (state) {
      case CostRadarSubscriptionState.TrialAvailable:
        return 'Trial Available';
      case CostRadarSubscriptionState.TrialStarted:
        return 'Trial Started';
      case CostRadarSubscriptionState.TrialExpired:
        return 'Trial Expired';
      case CostRadarSubscriptionState.Paid:
        return 'Paid';
      default:
        return state;
    }
  };

  const getStateColor = (state: CostRadarSubscriptionState) => {
    switch (state) {
      case CostRadarSubscriptionState.TrialAvailable:
        return 'primary';
      case CostRadarSubscriptionState.TrialStarted:
        return 'success';
      case CostRadarSubscriptionState.TrialExpired:
        return 'error';
      case CostRadarSubscriptionState.Paid:
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box width="100%" p={3} display="flex" justifyContent="center">
        <Box width="100%" maxWidth={800}>
          <Typography variant="h4" gutterBottom>
            Cost Radar Trial Management
          </Typography>

          {/* Venue Selection */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Venue
            </Typography>
            <Autocomplete<VenueOption>
              fullWidth
              value={selectedVenue}
              options={venues}
              getOptionLabel={(o) => o.name}
              loading={venuesLoading}
              onChange={handleVenueChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Venue"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: venuesLoading ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              )}
            />
          </Card>

          {/* Current Status Display */}
          {selectedVenueId && (
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Current Trial Status
              </Typography>
              {costRadarLoading ? (
                <CircularProgress />
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>State:</strong>{' '}
                    <span
                      style={{
                        color: (() => {
                          const stateColor = getStateColor(currentState);
                          if (stateColor === 'error') return 'red';
                          if (stateColor === 'success') return 'green';
                          if (stateColor === 'info') return 'blue';
                          return 'inherit';
                        })(),
                      }}
                    >
                      {getStateLabel(currentState)}
                    </span>
                  </Typography>
                  {currentSubscription?.trialStartedAt && (
                    <Typography variant="body1">
                      <strong>Started At:</strong>{' '}
                      {DateTime.fromMillis(
                        currentSubscription.trialStartedAt,
                      ).toFormat('MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                  {currentExpiresAt && (
                    <Typography variant="body1">
                      <strong>Expires At:</strong>{' '}
                      {currentExpiresAt.toFormat('MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                  {currentSubscription?.paidAt && (
                    <Typography variant="body1">
                      <strong>Paid At:</strong>{' '}
                      {DateTime.fromMillis(currentSubscription.paidAt).toFormat(
                        'MMM dd, yyyy HH:mm',
                      )}
                    </Typography>
                  )}
                  {!currentSubscription && (
                    <Typography variant="body2" color="text.secondary">
                      No subscription record exists for this venue. Trial is
                      available to start.
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
          )}

          {/* Trial Controls */}
          {selectedVenueId && !costRadarLoading && (
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Update Trial Status
              </Typography>

              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Trial State</InputLabel>
                  <Select
                    value={trialState}
                    label="Trial State"
                    onChange={handleStateChange}
                  >
                    <MenuItem value={CostRadarSubscriptionState.TrialAvailable}>
                      Trial Available
                    </MenuItem>
                    <MenuItem value={CostRadarSubscriptionState.TrialStarted}>
                      Trial Started
                    </MenuItem>
                    <MenuItem value={CostRadarSubscriptionState.TrialExpired}>
                      Trial Expired
                    </MenuItem>
                    <MenuItem value={CostRadarSubscriptionState.Paid}>
                      Paid
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {(trialState === CostRadarSubscriptionState.TrialStarted ||
                trialState === CostRadarSubscriptionState.TrialExpired) && (
                <Box sx={{ mb: 2 }}>
                  <DatePicker
                    label="Trial Expiration Date"
                    value={expirationDate}
                    onChange={(newValue: DateTime | null) =>
                      setExpirationDate(newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </Box>
              )}

              <Button
                variant="contained"
                onClick={openConfirmDialog}
                disabled={!trialState}
                sx={{ mt: 2 }}
              >
                Update Trial Status
              </Button>
            </Card>
          )}

          {/* Confirmation Dialog */}
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Trial Status Update</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to update the trial status for{' '}
                {selectedVenue?.name} to{' '}
                <strong>{trialState && getStateLabel(trialState)}</strong>
                {expirationDate && (
                  <>
                    {' '}
                    with expiration date{' '}
                    <strong>{expirationDate.toFormat('MMM dd, yyyy')}</strong>
                  </>
                )}
                ?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirm} variant="contained">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
